import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "animate", "Content", "renderContent", "disabled", "flush", "isCardHeader", "onOpenChange", "open", "title", "size", "titleTagName", "defaultOpen"];
import { filterProps } from '../../../utils/filterProps';
import { useMapStylePropsToCSS } from '../../../hooks/useMapStylePropsToCSS';
import { AnimatePresence, m } from 'framer-motion';
import styled, { useTheme } from 'styled-components';
import { sizeToHeading, getPaddingLeft, getMarginBottom, getIconSize } from './AccordionItem.helpers';
import { RightIcon } from 'foundations-assets';
import { Text } from '../../../components/Text';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { Clickable } from '../Clickable/Clickable';
import { useCardContext } from '../Card/CardContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledClickable = styled(Clickable)(({
  $size,
  $titleTagName,
  $flush,
  $isCardHeader,
  disabled,
  theme: _theme
}) => {
  const theme = _theme;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    userSelect: 'none',
    marginBottom: $flush || $isCardHeader ? theme.space['0'] : theme.space[getMarginBottom($size, $titleTagName)],
    '> h1, > h2, > h3, > h4, > h5, > h6, > span': Object.assign({}, disabled ? {
      color: theme.color['text-core-disabled']
    } : {
      '&:hover': {
        color: theme.color['text-interactive-default-hover']
      }
    })
  };
});
const ExpandableCaret = ({
  open,
  size,
  titleTagName
}) => {
  const theme = useTheme();
  return /*#__PURE__*/_jsx("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      width: theme.space[getPaddingLeft(size, titleTagName)]
    },
    children: /*#__PURE__*/_jsx(m.span, {
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      initial: false,
      animate: {
        rotate: open ? 90 : 0
      },
      children: /*#__PURE__*/_jsx(RightIcon, {
        color: "icon-interactive-hover",
        size: getIconSize(size, titleTagName)
      })
    })
  });
};
export const AccordionItem = _ref => {
  let {
      children,
      animate = true,
      Content,
      renderContent,
      disabled = false,
      flush = false,
      isCardHeader,
      onOpenChange,
      open = false,
      title,
      size = 'xs',
      titleTagName,
      defaultOpen
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    domProps,
    styleProps
  } = filterProps(rest);
  const cssStyles = useMapStylePropsToCSS(styleProps);
  const {
    compact,
    expandable
  } = useCardContext();
  const {
    isOpen,
    onOpen,
    onClose,
    triggerElementAriaAttributes
  } = useDisclosure({
    defaultIsOpen: defaultOpen || open,
    disabled,
    onOpen: () => {
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(true);
    },
    onClose: () => {
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
    }
  });
  const heading = titleTagName || sizeToHeading[size];
  let renderedContent = null;
  if (Content) {
    renderedContent = /*#__PURE__*/_jsx(Content, {});
  } else if (renderContent) {
    renderedContent = renderContent();
  } else {
    renderedContent = children;
  }
  const renderCaret = isCardHeader ? !compact || expandable : true;
  return /*#__PURE__*/_jsxs("div", Object.assign({}, domProps, {
    style: cssStyles,
    children: [/*#__PURE__*/_jsxs(StyledClickable, Object.assign({}, triggerElementAriaAttributes, {
      $size: size,
      $titleTagName: titleTagName,
      $flush: flush,
      $isCardHeader: isCardHeader,
      disabled: disabled,
      onClick: () => {
        if (isOpen) {
          onClose();
        } else {
          onOpen();
        }
      },
      children: [renderCaret && /*#__PURE__*/_jsx(ExpandableCaret, {
        open: isOpen || open,
        size: size,
        titleTagName: titleTagName
      }), /*#__PURE__*/_jsx(Text, {
        as: heading,
        children: title
      })]
    })), /*#__PURE__*/_jsx(AnimatePresence, {
      children: (isOpen || open) && /*#__PURE__*/_jsx(m.section, Object.assign({}, animate && {
        // When the content is removed from the screen
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: [0.42, 0, 0.58, 1]
        }
      }, {
        style: {
          paddingLeft: theme.space[getPaddingLeft(size, titleTagName)]
        },
        children: renderedContent
      }))
    })]
  }));
};